
import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    consultationType: '',
    preferredTime: ''
  });

  const [showConsultationPayment, setShowConsultationPayment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const consultationFees = {
    'investment-consultation': 150,
    'property-development': 200,
    'business-planning': 175,
    'market-analysis': 125
  };

  const consultationNames = {
    'investment-consultation': 'Investment Strategy Consultation',
    'property-development': 'Property Development Consultation',
    'business-planning': 'Business Planning Session',
    'market-analysis': 'Market Analysis Review'
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.consultationType && consultationFees[formData.consultationType as keyof typeof consultationFees]) {
      setShowConsultationPayment(true);
    } else {
      await submitContactForm();
    }
  };

  const handlePayConsultation = async () => {
    setIsSubmitting(true);
    
    try {
      const fee = consultationFees[formData.consultationType as keyof typeof consultationFees];
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: fee,
          currency: 'usd',
          description: consultationNames[formData.consultationType as keyof typeof consultationNames],
          metadata: {
            consultation_type: formData.consultationType,
            customer_name: formData.name,
            customer_email: formData.email,
            customer_phone: formData.phone,
            preferred_time: formData.preferredTime,
            subject: formData.subject
          }
        })
      });

      const { url } = await response.json();
      if (url) {
        // Also submit form data
        await submitContactForm();
        window.location.href = url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('There was an error processing your payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitContactForm = async () => {
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d42hb66mj2r470qad2ng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        alert('Thank you for your message! We will get back to you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          consultationType: '',
          preferredTime: ''
        });
        setShowConsultationPayment(false);
      } else {
        alert('There was an error sending your message. Please try again.');
      }
    } catch (error) {
      alert('There was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const selectedFee = formData.consultationType ? consultationFees[formData.consultationType as keyof typeof consultationFees] : 0;

  if (showConsultationPayment && formData.consultationType) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Consultation Booking</h3>
        
        <div className="bg-emerald-50 rounded-lg p-6 mb-8">
          <h4 className="text-xl font-semibold text-emerald-800 mb-4">Consultation Details</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-emerald-700">Service:</span>
              <span className="font-semibold text-emerald-800">
                {consultationNames[formData.consultationType as keyof typeof consultationNames]}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-700">Duration:</span>
              <span className="font-semibold text-emerald-800">60 minutes</span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-700">Preferred Time:</span>
              <span className="font-semibold text-emerald-800">{formData.preferredTime || 'To be scheduled'}</span>
            </div>
            <div className="border-t border-emerald-200 pt-2 mt-4">
              <div className="flex justify-between">
                <span className="text-emerald-700 text-lg">Consultation Fee:</span>
                <span className="font-bold text-emerald-800 text-xl">${selectedFee}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <button
            onClick={handlePayConsultation}
            disabled={isSubmitting}
            className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {isSubmitting ? 'Processing...' : `Pay & Book Consultation ($${selectedFee})`}
          </button>
          
          <button
            onClick={() => setShowConsultationPayment(false)}
            className="w-full bg-white text-gray-600 py-3 px-6 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Form
          </button>
        </div>

        <p className="text-sm text-gray-600 text-center mt-6">
          After payment, we'll contact you within 24 hours to schedule your consultation.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h3>
      
      <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
          />
        </div>

        <div>
          <label htmlFor="consultationType" className="block text-sm font-medium text-gray-700 mb-2">
            Consultation Type (Optional - Paid Service)
          </label>
          <select
            id="consultationType"
            name="consultationType"
            value={formData.consultationType}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
          >
            <option value="">General Inquiry (Free)</option>
            <option value="investment-consultation">Investment Strategy Consultation - $150</option>
            <option value="property-development">Property Development Consultation - $200</option>
            <option value="business-planning">Business Planning Session - $175</option>
            <option value="market-analysis">Market Analysis Review - $125</option>
          </select>
        </div>

        {formData.consultationType && (
          <div>
            <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Time
            </label>
            <input
              type="text"
              id="preferredTime"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              placeholder="e.g., Weekdays 2-4 PM EST, or specific date/time"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            />
          </div>
        )}

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
            Subject *
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={6}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
            placeholder="Please describe your inquiry or project details..."
          />
          <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? 'Sending...' : formData.consultationType ? 'Continue to Payment' : 'Send Message'}
        </button>
      </form>
    </div>
  );
}
